import React from "react";

import moment from "moment";

import {
  formatNumber,
  useGetPracticeMineralFertilizers,
  useGetPracticeOrganicFertilizers,
  useGetPracticeSeedings,
  useGetPracticeTillages,
  useTranslation,
} from "utils";

import { getUnitDisplayLabel } from "consts";

import { FieldListField, PRACTICE_SLUG } from "models";

import { Flex, Icon, Tooltip, Typography } from "components";

import { type MissingDataForm, YIELD_PSEUDO_PRACTICE_TYPE } from "pages/app/historical-practice-data-table-view/components/AddEditMissingDataDrawer.tsx";
import { FieldMap } from "pages/app/historical-practice-data-table-view/components/FieldMap";
import {
  BULK_CROP_DATA_POINT,
  type BulkFieldBreakdownChip,
  type BulkFieldBreakdownRow,
} from "pages/app/historical-practice-data-table-view/utils/BulkDataPointUtils";

export type BulkFieldBreakdownProps = {
  rows: BulkFieldBreakdownRow[];
  fields: FieldListField[];
};

// the drawer header's "Show details" body: one card per selected crop, listing where that crop stands on every data
// point the drawer offers, so the farmer can see what the save will and won't touch before picking a card
export const BulkFieldBreakdown = ({ rows, fields }: BulkFieldBreakdownProps) => {
  const { t } = useTranslation("farm");

  return (
    <Flex vertical gap="0.5rem" className="bulk-field-breakdown">
      {rows.map((row) => {
        const field = fields.find((candidate) => candidate.id === row.fieldId);

        return (
          <Flex key={row.cropId} gap="0.75rem" className="bulk-field-breakdown-row" data-testid={`bulk-field-breakdown-row-${row.cropId}`}>
            {field && <FieldMap field={field} />}
            <Flex vertical flex="1" gap="0.5rem">
              <Flex align="center" gap="0.5rem">
                <Typography variant="paragraph-m" bold>
                  {field?.name || t("farm:unknownFieldName")}
                </Typography>
                <Typography variant="paragraph-s" color="grey-600">
                  {formatNumber({ number: field?.size ?? 0 })} ha
                </Typography>
              </Flex>
              <Flex wrap gap="0.5rem">
                {row.chips.map((chip) => (
                  <BulkFieldBreakdownStatusChip
                    key={chip.practiceType}
                    chip={chip}
                    cropName={row.cropName}
                    cropTranslationSlug={row.cropTranslationSlug}
                    missingDataFormItem={row.missingDataFormItem}
                  />
                ))}
              </Flex>
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
};

const BulkFieldBreakdownStatusChip = ({
  chip,
  cropName,
  cropTranslationSlug,
  missingDataFormItem,
}: {
  chip: BulkFieldBreakdownChip;
  cropName: string;
  cropTranslationSlug: string;
  missingDataFormItem: MissingDataForm;
}) => {
  const { t } = useTranslation(["historicalPracticeDataTableView", "practices", "allFieldView"]);

  // the chips use their own shorter wording than the data point cards ("Yield" rather than "Fresh yield"), and the
  // crop chip names the crop itself
  const label =
    chip.practiceType === BULK_CROP_DATA_POINT.cropName
      ? t(`practices:crops.${cropTranslationSlug}`, { defaultValue: cropName })
      : t(`historicalPracticeDataTableView:bulkDataDrawer.chips.${chip.labelKey}`);

  const value = useChipValue(chip, missingDataFormItem);
  // a locked chip says why it can't be edited even when the crop holds nothing worth naming
  const tooltip = chip.status === "locked" ? [value, t("historicalPracticeDataTableView:bulkDataDrawer.chips.notEditable")].filter(Boolean).join(" — ") : value;

  const chipContent = (
    <Flex
      align="center"
      gap="0.25rem"
      className={`bulk-field-breakdown-chip ${chip.status}`}
      data-testid={`bulk-field-breakdown-chip-${chip.practiceType.toLowerCase()}`}
    >
      {chip.status === "locked" ? (
        <Icon iconName="tabler/lock" size="xs" className="bulk-field-breakdown-chip-lock" />
      ) : (
        <span className="bulk-field-breakdown-chip-dot" />
      )}
      <Typography variant="paragraph-xs" className="bulk-field-breakdown-chip-label">
        {chip.additionalEntryCount > 0 ? `${label} (+${chip.additionalEntryCount})` : label}
      </Typography>
    </Flex>
  );

  if (!tooltip) return chipContent;

  return (
    <Tooltip variant="neutral" placement="top" title={tooltip}>
      {chipContent}
    </Tooltip>
  );
};

// what the crop currently holds for a data point, in the same wording the forms use. Undefined where there is
// nothing to show — an empty data point's chip says all there is to say in its label
const useChipValue = (chip: BulkFieldBreakdownChip, item: MissingDataForm): string | undefined => {
  const { t } = useTranslation(["practices", "allFieldView"]);
  const tillagesQuery = useGetPracticeTillages({ enabled: true });
  const seedingsQuery = useGetPracticeSeedings({ enabled: true });
  const organicFertilizersQuery = useGetPracticeOrganicFertilizers({ enabled: true });
  const mineralFertilizersQuery = useGetPracticeMineralFertilizers({ enabled: true });

  const namesOf = (practiceIds: string[], practices: Array<{ id: string; name: string }> | undefined) =>
    practiceIds
      .map((practiceId) => practices?.find((practice) => practice.id === practiceId)?.name)
      .filter(Boolean)
      .join(", ") || undefined;

  switch (chip.practiceType) {
    case BULK_CROP_DATA_POINT.cropName:
      return item.crop?.cropName || undefined;
    case BULK_CROP_DATA_POINT.residue:
      return item.crop?.hasResidue == null ? undefined : t(item.crop.hasResidue ? "allFieldView:cropForm.yes" : "allFieldView:cropForm.no");
    case PRACTICE_SLUG.CROPS:
      return item.crop?.seedingStartDate && item.crop?.harvestEndDate
        ? // the same wording the table row uses for a growing period
          `${moment(item.crop.seedingStartDate).format("DD MMM")} – ${moment(item.crop.harvestEndDate).format("DD MMM")}`
        : undefined;
    case YIELD_PSEUDO_PRACTICE_TYPE:
      return item.yieldPerHa == null ? undefined : `${formatNumber({ number: item.yieldPerHa })} ${getUnitDisplayLabel(item.yieldPerHaUnit)}`;
    case PRACTICE_SLUG.SEEDINGS:
      return namesOf(item.seedings ?? [], seedingsQuery.data);
    case PRACTICE_SLUG.TILLAGES:
      return namesOf(
        (item.tillages ?? []).map((tillage) => tillage.assignedPracticeId),
        tillagesQuery.data
      );
    case PRACTICE_SLUG.ORGANIC_FERTILIZERS:
      return namesOf(
        (item.organicFertilizers ?? []).map((fertilizer) => fertilizer.assignedPracticeId),
        organicFertilizersQuery.data
      );
    case PRACTICE_SLUG.MINERAL_FERTILIZERS:
      return namesOf(
        (item.mineralFertilizers ?? []).map((fertilizer) => fertilizer.assignedPracticeId),
        mineralFertilizersQuery.data
      );
    default:
      return undefined;
  }
};
